import { ActionIcon, Stack, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { Product, Role, useDeleteProductMutation, useGetProductsQuery } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/produtos/")({
  component: RouteComponent,
  beforeLoad: requireRoles({ allowed: [Role.Usuario] })
});

function RouteComponent() {
  const navigate = useNavigate();
  const { data = [], isFetching, refetch } = useGetProductsQuery();
  const [trigger, { isLoading }] = useDeleteProductMutation();

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: "Confirmação de Exclusão",
      children: <p>Tem certeza que deseja excluir o produto?</p>,
      labels: { confirm: "Excluir", cancel: "Cancelar" },
      groupProps: { className: cn("grid! grid-cols-1 md:grid-cols-2") },
      cancelProps: { disabled: isLoading },
      confirmProps: { color: "red", loading: isLoading },
      onConfirm: async () => {
        await trigger({ id });
      }
    });
  };

  const columns: GridColDef<Product>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 100,
      type: "number"
    },
    {
      field: "name",
      headerName: "Produto",
      flex: 1,
      display: "flex",
      minWidth: 475,
      type: "string",
      renderCell: ({ row }) => {
        const { code, name } = row;

        return (
          <div className="flex flex-col">
            <p>
              <strong>Código:</strong> {code}
            </p>
            <p>{name}</p>
          </div>
        );
      }
    },
    {
      field: "group",
      headerName: "Grupo",
      minWidth: 130,
      flex: 1,
      valueGetter: (val) => (val ? "SIDERURGICA" : "AGRICOLA")
    },
    {
      field: "ncmcode",
      headerName: "NCM Código",
      minWidth: 125,
      type: "string"
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: ({ row }) => {
        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Produto">
              <ActionIcon variant="light" color="yellow" onClick={() => navigate({ to: "/dashboard/produtos/editar/$id/modal", params: { id: row.id } })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Produto">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete(row.id)} size="md">
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between gap-[inherit]">
        <DashboardComponents.Title>Gerenciar Produtos</DashboardComponents.Title>

        <DashboardComponents.RefreshButton onClick={refetch} tooltipProps={{ label: "Atualizar Produtos" }} loading={isFetching} />
      </div>

      <DataGridBase
        rows={data}
        columns={columns}
        columnVisibilityModel={{
          id: false
        }}
        loading={isFetching}
      />

      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
    </Stack>
  );
}
